import { DataTablePagingProps } from '@zaiusinc/hera';
import { action, computed, observable, reaction } from 'mobx';
import { ZipApi } from '../lib/ZipApi';
import { DataSyncItem, SyncStrategy, OptimizelyProduct } from '../types/DataSyncItem';
import { directoryStore } from './DirectoryStore';
import { DataSyncStrategyProvider } from '../pages/DataSync/DataSyncStrategyProvider';
import {Sort, SortDirection} from '../types/Sort';

export class DataSyncStore {
  public static readonly MAX_DATA_SYNCS = 500;
  @observable public searchFilter: string = '';
  @observable public filterByStatus: string = 'all';
  @observable public loading = false;

  @observable private _allDataSyncs: DataSyncItem[] = [];
  @observable private _paging: DataTablePagingProps = {
    rowStart: 1,
    rowCount: DataSyncStore.MAX_DATA_SYNCS,
    total: DataSyncStore.MAX_DATA_SYNCS,
  };
  @observable private _sort: Sort = {
    field: 'updatedAt',
    direction: SortDirection.DESC
  };
  private _canTriggerSyncCache = new Map<string, boolean>();

  constructor() {
    reaction(
      () => directoryStore.trackerId,
      () => {
        if (directoryStore.trackerId) {
          this.fetch();
        }
      }
    );
  }

  public async fetch() {
    try {
      this.loading = true;
      this._allDataSyncs = [];
      this._canTriggerSyncCache.clear(); // Clear cache when fetching new data
      const offset = Math.floor(this._paging.rowStart / this._paging.rowCount);
      const response = await ZipApi.listDataSyncs(
        offset,
        this._paging.rowCount,
        this.sort,
        directoryStore.trackerId
      );
      this._allDataSyncs =
        response.dataSyncItems?.map((item) => new DataSyncItem(item)) || [];
    } finally {
      this.loading = false;
    }
  }

  @action
  public async toggleDataSync(dataSyncId: string) {
    const dataSync = this._allDataSyncs.find((item) => item.id === dataSyncId);
    if (dataSync) {
      const func = dataSync.enabled
        ? ZipApi.disableDataSync.bind(ZipApi)
        : ZipApi.enableDataSync.bind(ZipApi);
      if (await func(dataSyncId)) {
        dataSync.enabled = !dataSync.enabled;
      }
    }
  }

  @computed
  public get dataSyncCount(): number {
    return this._allDataSyncs.length;
  }

  @computed
  public get dataSyncs(): DataSyncItem[] {
    let filteredList = this._allDataSyncs;

    const searchTerm = this.searchFilter.trim().toLowerCase();
    if (searchTerm) {
      filteredList = filteredList.filter(
        ({ name, source, destination }: DataSyncItem) =>
          [name, source.object.object_name, destination.object.object_name].some((field) =>
            field.toLowerCase().includes(searchTerm)
          )
      );
    }

    if (this.filterByStatus !== 'all') {
      filteredList = filteredList.filter(
        (data: DataSyncItem) => {
          if (this.filterByStatus === 'active') {
            return data.enabled || DataSyncStrategyProvider.getStrategy(data) === SyncStrategy.WEBHOOK;
          } else {
            return DataSyncStrategyProvider.getStrategy(data) === SyncStrategy.SCHEDULED && !data.enabled;
          }
        }
      );
    }

    return filteredList;
  }

  @action
  public async createDataSync(dataSync: DataSyncItem) {
    try {
      this.loading = true;
      const response = await ZipApi.createDataSync(dataSync);
      this._allDataSyncs.unshift(response.dataSync);
    } finally {
      this.loading = false;
    }
  }

  @action
  public async getDataSync(dataSyncId: string) {
    try {
      this.loading = true;
      const { dataSync } = await ZipApi.getDataSync(dataSyncId);
      return dataSync;
    } finally {
      this.loading = false;
    }
  }

  @action
  public async updateDataSync(dataSync: DataSyncItem) {
    try {
      this.loading = true;
      const response = await ZipApi.updateDataSync(dataSync);
      const index = this._allDataSyncs.findIndex(
        (item) => item.id === dataSync.id
      );
      if (index !== -1) {
        this._allDataSyncs[index] = response.dataSync;
      }
    } finally {
      this.loading = false;
    }
  }

  @action
  public async deleteDataSync(dataSyncId: string) {
    await ZipApi.deleteDataSync(dataSyncId);
    this._allDataSyncs = this._allDataSyncs.filter(
      (item) => item.id !== dataSyncId
    );
  }

  @action
  public async triggerSync(dataSync: DataSyncItem) {
    // We get just the first job name from configured in a source
    // This needs to be changed after moving to the new design to support job selection
    const sourceName = dataSync.source.object.object_name;
    let jobName;
    if (!directoryStore.trackerId || dataSync.source.product !== OptimizelyProduct.HUB_APP) {
      jobName = undefined;
    } else {
      const sources = await ZipApi.listAppSources(directoryStore.trackerId, dataSync.source?.instance?.id);
      const sourceJob = sources.find((source) => source.name === sourceName);
      jobName = sourceJob?.jobs?.[0].name;
    }
    return await ZipApi.triggerSync(dataSync.id, jobName);
  }

  public async canTriggerSync(dataSync: DataSyncItem): Promise<boolean> {
    const cacheKey = dataSync.id;
    if (this._canTriggerSyncCache.has(cacheKey)) {
      return this._canTriggerSyncCache.get(cacheKey)!;
    }

    if (dataSync.source.product !== OptimizelyProduct.HUB_APP) {
      this._canTriggerSyncCache.set(cacheKey, true);
      return true;
    }
    if (!directoryStore.trackerId) {
      // we don't put this value to cache as the trackerId should be there
      // maybe the request came about before the directory store had the trackerId
      // we'll give it a pass so that next calls are validated from the ZipApi
      return false;
    }
    const sources = await ZipApi.listAppSources(directoryStore.trackerId, dataSync.source?.instance?.id);
    const sourceJob = sources.find((source) => source.name === dataSync.source.object.object_name);
    const result = !!sourceJob?.jobs?.[0].name;

    this._canTriggerSyncCache.set(cacheKey, result);
    return result;
  }

  @computed
  public get paging() {
    return this._paging;
  }

  @action
  public setPaging(paging: DataTablePagingProps) {
    if (paging.rowStart <= 0) {
      paging.rowStart = 1;
    }
    this._paging = paging;
    this.fetch();
  }

  @computed
  get sort(): Sort {
    return this._sort;
  }

  @action
  public setSortField(field: string, ) {
    this._sort = {...this._sort, field};
    this.fetch();
  }

  @action
  public setSortDirection(direction: SortDirection) {
    this._sort = {...this._sort, direction};
    this.fetch();
  }
}

export const dataSyncStore = new DataSyncStore();
